import { cmsFields } from "./fields.js";
import {
  loadCms,
  getCmsBranches,
  getBranchList,
  getDefaultBranches,
  saveCms,
  sanitizeCmsStorage,
} from "./store.js";
import { resolveBranchImageSrc } from "./branch-images.js";
import { resolveImageSrc, shouldPersistImage } from "./images.js";
import { syncHomeSeoMeta } from "../seo.js";
import { resolveHomeSeoDescription } from "./seo-description.js";

export function applyCms() {
  sanitizeCmsStorage();
  const cms = loadCms();

  cmsFields.forEach((field) => {
    applyFieldValue(field, cms);
  });

  applyLinks(cms.links);
  applyBranchFields();
  applyGlobalPhone(cms.text?.["global.phone"]);
  applyHomeSeoCmsPreview(cms);
  if (document.body.dataset.page === "home") {
    syncHomeSeoMeta(cms);
  }
}

function applyHomeSeoCmsPreview(cms) {
  const el = document.getElementById("seo-cms-description");
  if (!el) return;
  const explicit = cms.text?.["seo.home.description"]?.trim();
  el.textContent = explicit || resolveHomeSeoDescription(cms);
  el.hidden = !document.body.classList.contains("is-cms-editing");
}

function applyLinks(links) {
  if (!links) return;
  Object.entries(links).forEach(([key, url]) => {
    if (!url) return;
    document.querySelectorAll(`[data-cms-key="${key}"]`).forEach((el) => {
      el.setAttribute("href", url);
    });
  });
}

function applyFieldValue(field, cms) {
  const value =
    field.type === "image" ? cms.images?.[field.key] : cms.text?.[field.key];
  if (!value || (typeof value === "string" && !value.trim())) return;

  document.querySelectorAll(field.selector).forEach((el) => {
    setElementValue(el, field.type, value, field.attr);
  });
}

function setElementValue(el, type, value, attr) {
  if (type === "attr" && attr) {
    el.setAttribute(attr, value);
    return;
  }
  if (type === "link") {
    el.setAttribute("href", value);
    return;
  }
  if (type === "image") {
    const resolved = resolveImageSrc(value, el);
    if (resolved) el.setAttribute("src", resolved);
    return;
  }
  if (type === "html") {
    el.innerHTML = value;
    return;
  }
  el.textContent = value;
}

export function applyBranchFields() {
  const branches = getCmsBranches();

  document.querySelectorAll("[data-branch]").forEach((card) => {
    const id = card.dataset.branch;
    const data = branches[id];
    if (!data) return;

    card.querySelectorAll("[data-cms-branch-prop]").forEach((el) => {
      const prop = el.dataset.cmsBranchProp;
      const val = data[prop];
      if (!val || (typeof val === "string" && !val.trim())) return;
      const type = el.dataset.cmsType || (el.tagName === "IMG" ? "image" : "text");
      if (type === "image") {
        el.setAttribute("src", resolveBranchImageSrc(id, val));
        return;
      }
      setElementValue(el, type, val);
    });

    const heading = card.querySelector("h2:not([data-cms-branch-prop]), h3:not([data-cms-branch-prop])");
    if (heading && data.branch) heading.textContent = data.branch;

    const img = card.querySelector(".product-img:not([data-cms-branch-prop])");
    if (img && data.image) {
      img.setAttribute("src", resolveBranchImageSrc(id, data.image));
    }
  });

  const branchId = document.body.dataset.branchId;
  if (!branchId) return;

  const data = branches[branchId];
  if (!data) return;

  document.querySelectorAll("[data-cms-branch-prop]").forEach((el) => {
    const prop = el.dataset.cmsBranchProp;
    const val = data[prop];
    if (!val) return;
    const type = el.dataset.cmsType || (el.tagName === "IMG" ? "image" : "text");
    if (type === "image") {
      el.setAttribute("src", resolveBranchImageSrc(branchId, val));
      return;
    }
    setElementValue(el, type, val);
  });
}

export function mergeBranchWithCms(branch) {
  if (!branch?.id) return branch;
  const branches = getCmsBranches();
  return { ...branches[branch.id], ...branch, id: branch.id };
}

export function findBranchId(branch) {
  if (branch?.id) return branch.id;
  const branches = getCmsBranches();
  const match = Object.entries(branches).find(([, data]) => data.title === branch?.title);
  return match?.[0] ?? "";
}

export function applyGlobalPhone(phone) {
  if (!phone) return;
  const digits = phone.replace(/\s/g, "");
  document.querySelectorAll('a[href^="tel:"]').forEach((link) => {
    link.setAttribute("href", `tel:${digits}`);
    if (link.classList.contains("bm-nav__phone")) {
      const icon = link.querySelector(".bm-icon");
      link.textContent = "";
      if (icon) link.appendChild(icon);
      link.append(` ${phone}`);
    } else if (link.classList.contains("bm-footer__phone")) {
      link.textContent = phone;
    }
  });
}

export function persistBranchField(branchId, prop, value) {
  const cms = loadCms();
  const list = Array.isArray(cms.branchList)
    ? cms.branchList
    : getBranchList().map((branch) => ({ ...branch }));
  const index = list.findIndex((branch) => branch.id === branchId);
  if (index === -1) return;

  const defaults = getDefaultBranches()[branchId] ?? list[index];

  if (prop === "image" && !shouldPersistImage(value, defaults.image)) {
    delete list[index].image;
  } else {
    list[index] = { ...list[index], [prop]: value };
  }

  cms.branchList = list;
  saveCms(cms);

  try {
    const item = JSON.parse(localStorage.getItem("myitem") || "null");
    if (item?.id === branchId) {
      item[prop] = value;
      localStorage.setItem("myitem", JSON.stringify(item));
    }
  } catch {
    /* ignore */
  }
}
